import { Controller } from "@hotwired/stimulus"

// "Merci" reaction — optimistic flip + a celebratory burst of tiny emojis
// emanating from the button, plus an elastic scale on the label. Reverts to a
// quick contract on un-like. On server failure, rolls back to the previous
// state without re-triggering animations.
export default class extends Controller {
  static targets = ["btn", "icon", "count"]
  static values  = { postId: Number, liked: Boolean }

  toggle() {
    this._optimistic(`/feed/${this.postIdValue}/toggle_like`)
  }

  toggleComment() {
    this._optimistic(`/feed/comments/${this.postIdValue}/toggle_like`)
  }

  _optimistic(url) {
    const wasLiked = this.likedValue
    const prevCount = parseInt(this.countTarget?.textContent || "0", 10) || 0
    const nowLiked = !wasLiked
    const nextCount = Math.max(0, prevCount + (nowLiked ? 1 : -1))

    this._render(nowLiked, nextCount, /* animate */ true)
    this.likedValue = nowLiked

    fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Accept":       "application/json",
        "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")?.content || ""
      }
    })
      .then((r) => (r.ok ? r.json().catch(() => null) : null))
      .then((data) => {
        if (data && typeof data.liked === "boolean") {
          this.likedValue = data.liked
          this._render(data.liked, data.likes_count ?? nextCount, false)
        }
      })
      .catch(() => {
        this.likedValue = wasLiked
        this._render(wasLiked, prevCount, false)
      })
  }

  _render(liked, count, animate) {
    if (this.hasBtnTarget) this.btnTarget.classList.toggle("is-liked", liked)

    if (animate) {
      if (liked) {
        this._scaleLabel("liked")
        this._burst()
      } else {
        this._scaleLabel("unliked")
      }
    }

    if (this.hasCountTarget) {
      if (count > 0) {
        this.countTarget.textContent = String(count)
        this.countTarget.classList.remove("fp-hidden", "hidden")
      } else {
        this.countTarget.textContent = ""
        this.countTarget.classList.add("fp-hidden")
      }
    }
  }

  _scaleLabel(state) {
    const target = this.hasIconTarget ? this.iconTarget : this.btnTarget
    if (!target) return
    const keyframes = state === "liked"
      ? [
          { transform: "scale(1)" },
          { transform: "scale(1.45)" },
          { transform: "scale(0.85)" },
          { transform: "scale(1.1)" },
          { transform: "scale(1)" }
        ]
      : [
          { transform: "scale(1)" },
          { transform: "scale(0.7)" },
          { transform: "scale(1)" }
        ]
    try {
      target.animate(keyframes, { duration: 380, easing: "cubic-bezier(0.16, 1, 0.3, 1)", fill: "none" })
    } catch (_) { /* WAAPI not available */ }
  }

  _burst() {
    if (!this.hasBtnTarget) return
    const rect = this.btnTarget.getBoundingClientRect()
    const cx = rect.left + rect.width / 2
    const cy = rect.top + rect.height / 2

    const emojis = ["💚", "🙏", "✨", "💫", "🤍"]
    const count = 5
    const baseAngle = -Math.PI / 2     // straight up
    const spread    =  Math.PI / 1.6   // ~110° fan

    for (let i = 0; i < count; i++) {
      const el = document.createElement("span")
      el.textContent = emojis[i % emojis.length]
      el.style.position = "fixed"
      el.style.left = `${cx}px`
      el.style.top  = `${cy}px`
      el.style.transform = "translate(-50%, -50%) scale(0.6)"
      el.style.pointerEvents = "none"
      el.style.fontSize = "18px"
      el.style.zIndex = "1000"
      el.style.opacity = "0"
      el.style.transition = "opacity 700ms ease-out, transform 700ms cubic-bezier(0.16, 1, 0.3, 1)"
      document.body.appendChild(el)

      const angle = baseAngle + (i / (count - 1) - 0.5) * spread
      const distance = 48 + Math.random() * 28
      const dx = Math.cos(angle) * distance
      const dy = Math.sin(angle) * distance
      const rot = (Math.random() - 0.5) * 60

      // Two-phase: flash in, then drift away.
      requestAnimationFrame(() => {
        el.style.opacity = "1"
        el.style.transform = "translate(-50%, -50%) scale(1)"
        requestAnimationFrame(() => {
          el.style.transform = `translate(calc(-50% + ${dx}px), calc(-50% + ${dy}px)) scale(0.85) rotate(${rot}deg)`
          el.style.opacity = "0"
        })
      })

      setTimeout(() => el.remove(), 750)
    }
  }
}
